import React, { useEffect, useState, useCallback, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';
import SITE from '../api';
import { usePortal, PORTAL } from '../portalAuth';
import { useSite } from '../components/StoreLayout';
import { STORE_BASE } from '../routeBase';
import { ProductCard } from './Home';
import useSEO from '../useSEO';

const GRID_COLS_KEY = 'sf_shop_cols';
const FONT_SCALE_KEY = 'sf_shop_font';

export default function Shop() {
  let portal = null;
  try { portal = usePortal(); } catch (e) { portal = null; }
  const customer = portal?.customer;
  // Admin-editable buying-guide FAQ, scoped to what's actually being browsed
  // (material filter first, then category, else a general fallback — see
  // backend/services/shopFaqs.js) so a Metal listing doesn't show
  // Acetate-specific answers. Same resolver the crawler-facing FAQPage
  // JSON-LD uses, so a human visitor and a JS-blind AI crawler see the same
  // content for the same URL.
  const [faq, setFaq] = useState([]);
  const [faqRelatedBlogs, setFaqRelatedBlogs] = useState([]);
  const [sp, setSp] = useSearchParams();
  // filters/categories are already fetched once by StoreLayout for the
  // header/footer nav — read them from context instead of re-fetching, so a
  // Shop page load doesn't duplicate two of its eight API calls.
  const { filters, categories } = useSite();
  const [products, setProducts] = useState([]);
  const [total, setTotal] = useState(0);
  const [totalSkus, setTotalSkus] = useState(0);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [hasMore, setHasMore] = useState(true);
  const [offset, setOffset] = useState(0);
  const sentinelRef = useRef(null);
  const [cols, setCols] = useState(() => parseInt(localStorage.getItem(GRID_COLS_KEY), 10) || 3);
  const [fontScale, setFontScale] = useState(() => localStorage.getItem(FONT_SCALE_KEY) || 'md');
  // Filter sidebar becomes an off-canvas drawer below the sf-shop-filters
  // breakpoint (see StoreLayout's <style> block) — this just tracks whether
  // it's open; the grid-column count itself is capped for mobile by CSS
  // (.sf-shop-grid below), not here, so a 3/4-col desktop preference in
  // localStorage never forces a cramped grid on a phone.
  const [filtersOpen, setFiltersOpen] = useState(false);
  useEffect(() => {
    if (!filtersOpen) return;
    document.body.style.overflow = 'hidden';
    return () => { document.body.style.overflow = ''; };
  }, [filtersOpen]);
  const PER = 24;

  const changeCols = (n) => { setCols(n); localStorage.setItem(GRID_COLS_KEY, n); };
  const changeFontScale = (s) => { setFontScale(s); localStorage.setItem(FONT_SCALE_KEY, s); };

  const active = {
    category: sp.get('category') || '',
    gender: sp.get('gender') || '',
    material: sp.get('material') || '',
    frame_type: sp.get('frame_type') || '',
    shape: sp.get('shape') || '',
    brand: sp.get('brand') || '',
    // set by the AI search bar (e.g. "purple frames") — no sidebar checkbox
    // for this yet, but the filter is fully functional via the URL.
    color: sp.get('color') || '',
    search: sp.get('search') || '',
  };
  // "New Arrivals" is just this same listing with no filters, explicitly
  // sorted newest-first (?new=1) — linked from the homepage hero panel.
  const isNewArrivals = sp.get('new') === '1' && !Object.values(active).some(Boolean);

  useEffect(() => {
    SITE.get('/shop-faq', { params: { material: active.material, category: active.category } })
      .then(r => { setFaq(r.data?.faq || []); setFaqRelatedBlogs(r.data?.relatedBlogs || []); })
      .catch(() => { setFaq([]); setFaqRelatedBlogs([]); });
  }, [active.material, active.category]); // eslint-disable-line

  const BANNER_KEYS = ['gender', 'material', 'frame_type', 'shape', 'brand'];
  const activeBannerKeys = BANNER_KEYS.filter(k => active[k]);
  // Banner only shows for a single "shop by X" filter value — never for New
  // Arrivals, search, category browsing, or multi-filter combinations.
  const bannerKey = (!isNewArrivals && !active.search && !active.category && activeBannerKeys.length === 1)
    ? activeBannerKeys[0] : null;
  const [banner, setBanner] = useState(null);

  useEffect(() => {
    if (!bannerKey) { setBanner(null); return; }
    SITE.get('/shop-banner', { params: { key: bannerKey, value: active[bannerKey] } })
      .then(r => setBanner(r.data.banner))
      .catch(() => setBanner(null));
  }, [bannerKey, active[bannerKey]]); // eslint-disable-line

  // Continuous "load more" listing (no Page 1/Page 2 clicks) — appendOffset=0
  // means "fresh list" (filters just changed), anything higher appends onto
  // what's already on screen as the user scrolls near the bottom.
  const load = useCallback((appendOffset) => {
    const isAppend = appendOffset > 0;
    if (isAppend) setLoadingMore(true); else setLoading(true);
    const params = { limit: PER, offset: appendOffset };
    // only send non-empty filters (cleaner URLs, no empty-string edge cases)
    for (const [k, v] of Object.entries(active)) if (v) params[k] = v;
    if (isNewArrivals) params.sort = 'newest';
    // Logged-in customers get their own priced listing from the portal API
    // (adds r.price per model) — guests get the public, price-less listing.
    const client = customer ? PORTAL : SITE;
    client.get('/products', { params })
      .then(r => {
        const rows = r.data.products || [];
        const t = r.data.total || 0;
        setTotal(t);
        setTotalSkus(r.data.total_skus || 0);
        setProducts(prev => isAppend ? [...prev, ...rows] : rows);
        setHasMore(appendOffset + rows.length < t);
      })
      .catch(() => { if (!isAppend) { setProducts([]); setTotal(0); setTotalSkus(0); } setHasMore(false); })
      .finally(() => { if (isAppend) setLoadingMore(false); else setLoading(false); });
  }, [sp.toString(), customer]); // eslint-disable-line

  // filters (or login state) changed — start over from the top
  useEffect(() => { setOffset(0); setHasMore(true); load(0); }, [load]); // eslint-disable-line

  // infinite scroll: load the next batch once the sentinel below the grid
  // scrolls into view, same continuous-listing pattern as the reference site
  useEffect(() => {
    const el = sentinelRef.current;
    if (!el || !hasMore) return;
    const io = new IntersectionObserver(entries => {
      if (entries[0].isIntersecting && !loading && !loadingMore) {
        const next = offset + PER;
        setOffset(next);
        load(next);
      }
    }, { rootMargin: '600px' });
    io.observe(el);
    return () => io.disconnect();
  }, [offset, hasMore, loading, loadingMore, load]);

  const setFilter = (key, val) => {
    const next = new URLSearchParams(sp);
    if (val && next.get(key) !== val) next.set(key, val); else next.delete(key);
    setSp(next);
  };
  const clearAll = () => setSp(new URLSearchParams());

  const heading = isNewArrivals ? 'New Arrivals' : active.category || (active.gender && `${active.gender}${active.gender.endsWith('s') ? "'" : "'s"} Collection`) ||
    (active.search && `Search: "${active.search}"`) || 'All Products';
  const activeCount = Object.values(active).filter(Boolean).length;

  const catMeta = active.category ? categories.find(c => c.name === active.category) : null;
  useSEO({
    title: catMeta?.meta_title || heading,
    description: catMeta?.meta_description || catMeta?.description ||
      (active.search ? `Search results for "${active.search}" — Salvador Eyewear.` : `Shop ${heading} at Salvador Eyewear — wholesale eyeglass frames and sunglasses.`),
    keywords: catMeta?.meta_keywords,
    canonical: `${window.location.origin}${window.location.pathname}${window.location.search}`,
  });

  return (
    <>
      {banner && banner.image && (
        <div style={{ maxWidth: 1600, margin: '0 auto', padding: '24px 24px 0' }}>
          <ShopBanner banner={banner} />
        </div>
      )}
      {/* 1600 (was 1200) so the default 3-per-row grid actually grows on a
          wide monitor instead of capping out at ~290px cards regardless of
          screen size — the filter column stays a fixed 220px either way. */}
      <div style={{ maxWidth: 1600, margin: '0 auto', padding: '24px', display: 'flex', gap: 26, alignItems: 'flex-start' }}>

      {/* filter sidebar — sticky AND independently scrollable: without its
          own maxHeight/overflowY, expanding a group near the bottom (e.g.
          Brand) just grows the panel past the viewport, forcing a scroll
          past the entire product grid to reach the options that just
          appeared. Capping height to the viewport and scrolling internally
          keeps every expanded option reachable without leaving the sidebar.
          Hidden below the sf-shop-filters-desktop breakpoint (see
          StoreLayout's <style> block) — the "Filters" button + drawer
          further down take over on mobile instead of this fixed-220px
          column, which was overflowing the viewport on a phone. */}
      <aside className="sf-shop-filters-desktop" style={{ width: 220, flexShrink: 0, background: '#fff', border: '1px solid #eee', borderRadius: 12, padding: 16, position: 'sticky', top: 16, maxHeight: 'calc(100vh - 32px)', overflowY: 'auto' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
          <span style={{ fontWeight: 600, fontSize: 15 }}>Filters</span>
          {activeCount > 0 && <button onClick={clearAll} style={{ border: 'none', background: 'none', color: 'var(--sf-accent)', fontSize: 12, cursor: 'pointer' }}>Clear all</button>}
        </div>
        <FilterGroup label="Gender" name="gender" options={filters.gender} active={active.gender} onSet={setFilter} defaultOpen />
        <FilterGroup label="Material" name="material" options={filters.material} active={active.material} onSet={setFilter} defaultOpen />
        <FilterGroup label="Frame Type" name="frame_type" options={filters.frame_type} active={active.frame_type} onSet={setFilter} />
        <FilterGroup label="Shape" name="shape" options={filters.shape} active={active.shape} onSet={setFilter} />
        <FilterGroup label="Brand" name="brand" options={filters.brand} active={active.brand} onSet={setFilter} />
      </aside>

      {/* mobile filter drawer — same FilterGroup controls as the desktop
          sidebar above, just presented as a bottom sheet triggered by the
          sf-shop-filters-btn button (rendered further down, mobile-only). */}
      {filtersOpen && (
        <>
          <div className="sf-mobile-nav-backdrop" onClick={() => setFiltersOpen(false)} />
          <div className="sf-shop-filters-drawer" role="dialog" aria-label="Filters">
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '14px 18px', borderBottom: '1px solid #eee', flexShrink: 0 }}>
              <span style={{ fontWeight: 700, fontSize: 15 }}>Filters</span>
              <div style={{ display: 'flex', alignItems: 'center', gap: 14 }}>
                {activeCount > 0 && <button onClick={clearAll} style={{ border: 'none', background: 'none', color: 'var(--sf-accent)', fontSize: 13, cursor: 'pointer' }}>Clear all</button>}
                <button aria-label="Close filters" onClick={() => setFiltersOpen(false)}
                  style={{ border: 'none', background: 'none', fontSize: 22, lineHeight: 1, cursor: 'pointer', color: '#666', padding: 4 }}>
                  <i className="ti ti-x" />
                </button>
              </div>
            </div>
            <div style={{ overflowY: 'auto', flex: 1, padding: '0 18px' }}>
              <FilterGroup label="Gender" name="gender" options={filters.gender} active={active.gender} onSet={setFilter} defaultOpen />
              <FilterGroup label="Material" name="material" options={filters.material} active={active.material} onSet={setFilter} defaultOpen />
              <FilterGroup label="Frame Type" name="frame_type" options={filters.frame_type} active={active.frame_type} onSet={setFilter} />
              <FilterGroup label="Shape" name="shape" options={filters.shape} active={active.shape} onSet={setFilter} />
              <FilterGroup label="Brand" name="brand" options={filters.brand} active={active.brand} onSet={setFilter} />
            </div>
            <div style={{ padding: 16, borderTop: '1px solid #eee', flexShrink: 0 }}>
              <button onClick={() => setFiltersOpen(false)} className="sf-btn-outline"
                style={{ width: '100%', padding: '12px 0', borderRadius: 8, border: '1.5px solid var(--sf-accent)', fontWeight: 600, fontSize: 14, cursor: 'pointer' }}>
                Show {total.toLocaleString()} result{total === 1 ? '' : 's'}
              </button>
            </div>
          </div>
        </>
      )}

      {/* products */}
      <div style={{ flex: 1 }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16, flexWrap: 'wrap', gap: 10 }}>
          <div>
            <h1 style={{ fontSize: 22, fontWeight: 600, margin: 0 }}>{heading}</h1>
            <span style={{ fontSize: 13, color: '#888' }}>
              {total.toLocaleString()} model{total === 1 ? '' : 's'} — {totalSkus.toLocaleString()} SKU{totalSkus === 1 ? '' : 's'}
            </span>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 14 }}>
            {/* mobile-only — opens the drawer above; the desktop sidebar is
                hidden below this same breakpoint (sf-shop-filters-btn). */}
            <button className="sf-shop-filters-btn" onClick={() => setFiltersOpen(true)}
              style={{ display: 'none', alignItems: 'center', gap: 6, border: '1.5px solid var(--sf-accent)', color: 'var(--sf-accent)', background: '#fff', borderRadius: 8, padding: '7px 14px', fontSize: 13, fontWeight: 600, cursor: 'pointer' }}>
              <i className="ti ti-adjustments-horizontal" style={{ fontSize: 15 }} />
              Filters{activeCount > 0 ? ` (${activeCount})` : ''}
            </button>
            <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
              <span style={{ fontSize: 11.5, color: '#999' }}>Text</span>
              {[['sm', 'A', 11], ['md', 'A', 14], ['lg', 'A', 17]].map(([key, letter, size]) => (
                <button key={key} onClick={() => changeFontScale(key)} title={key === 'sm' ? 'Small text' : key === 'lg' ? 'Large text' : 'Medium text'}
                  className={`sf-btn-outline${fontScale === key ? ' active' : ''}`}
                  style={{ width: 26, height: 26, borderRadius: 6, border: '1.5px solid var(--sf-accent)',
                    fontSize: size, fontWeight: 700, cursor: 'pointer', lineHeight: 1 }}>{letter}</button>
              ))}
            </div>
            <div className="sf-shop-gridsize" style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
              <span style={{ fontSize: 11.5, color: '#999' }}>Grid</span>
              {[2, 3, 4].map(n => (
                <button key={n} onClick={() => changeCols(n)} title={`${n} per row`}
                  className={`sf-btn-outline${cols === n ? ' active' : ''}`}
                  style={{ width: 30, height: 26, borderRadius: 6, border: '1.5px solid var(--sf-accent)',
                    fontSize: 12.5, fontWeight: 700, cursor: 'pointer' }}>{n}</button>
              ))}
            </div>
          </div>
        </div>

        {loading ? (
          <div style={{ padding: 60, textAlign: 'center', color: '#aaa' }}>Loading…</div>
        ) : products.length === 0 ? (
          <div style={{ padding: 60, textAlign: 'center', color: '#aaa', background: '#fff', borderRadius: 12, border: '1px solid #eee' }}>
            No products match these filters. <button onClick={clearAll} style={{ border: 'none', background: 'none', color: 'var(--sf-accent)', cursor: 'pointer' }}>Clear filters</button>
          </div>
        ) : (
          <div className="sf-shop-grid" style={{ display: 'grid', gridTemplateColumns: `repeat(${cols}, 1fr)`, gap: 16 }}>
            {products.map(p => <ProductCard key={p.model_no} p={p} cols={cols} fontScale={fontScale} />)}
          </div>
        )}

        {/* Continuous listing — the sentinel below triggers loading the next
            batch as it scrolls into view, no Page 1/Page 2 clicks needed. */}
        {!loading && products.length > 0 && (
          <div ref={sentinelRef} style={{ textAlign: 'center', padding: '30px 0 10px' }}>
            {loadingMore
              ? <span style={{ fontSize: 13, color: '#aaa' }}>Loading more…</span>
              : !hasMore && <span style={{ fontSize: 13, color: '#bbb' }}>You've reached the end — {total.toLocaleString()} model{total === 1 ? '' : 's'}, {totalSkus.toLocaleString()} SKU{totalSkus === 1 ? '' : 's'} in total</span>}
          </div>
        )}
      </div>
      </div>

      {faq.length > 0 && (
        <div style={{ maxWidth: 900, margin: '10px auto 40px', padding: '0 24px' }}>
          <h2 style={{ fontSize: 19, fontWeight: 600, marginBottom: 14 }}>Frequently Asked Questions</h2>
          {faq.map((f, i) => (
            <details key={i} style={{ borderTop: '1px solid #eee', padding: '12px 0' }}>
              <summary style={{ fontSize: 14.5, fontWeight: 600, cursor: 'pointer', color: 'var(--sf-primary)' }}>{f.question}</summary>
              <p style={{ fontSize: 13.5, color: '#555', lineHeight: 1.6, margin: '8px 0 0' }}>{f.answer}</p>
            </details>
          ))}
          {faqRelatedBlogs.length > 0 && (
            <div className="sf-related-reading" style={{ marginTop: 20, paddingTop: 16, borderTop: '1px solid #eee' }}>
              <div style={{ fontSize: 12.5, fontWeight: 700, color: '#888', textTransform: 'uppercase', letterSpacing: '.03em', marginBottom: 8 }}>Related Reading</div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                {faqRelatedBlogs.map(b => (
                  <a key={b.slug} href={`${STORE_BASE}/blog/${b.slug}`} style={{ fontSize: 13.5, color: 'var(--sf-primary)', textDecoration: 'none' }}>
                    → {b.title}
                  </a>
                ))}
              </div>
            </div>
          )}
        </div>
      )}
    </>
  );
}

const BANNER_LABELS = { gender: 'Gender', material: 'Material', frame_type: 'Frame Type', shape: 'Shape', brand: 'Brand' };

function ShopBanner({ banner }) {
  const img = banner.image.startsWith('/') ? banner.image : `/product-images/${banner.image}`;
  const text = banner.heading || `Eyewear ${BANNER_LABELS[banner.filter_key] || ''} — ${banner.filter_value}`;
  return (
    // aspectRatio matches the 1600x400 "best size" hint in ShopBanners admin
    // — a fixed height with a fluid, up-to-1600px-wide container meant the
    // image had to grow wider than its native shape to fill the box, so
    // cover silently zoomed in and cropped the top/bottom.
    <div style={{
      width: '100%', borderRadius: 14, overflow: 'hidden', position: 'relative',
      minHeight: 160, aspectRatio: '1600 / 400', background: '#f0f0f0', display: 'flex', alignItems: 'center', justifyContent: 'center',
    }}>
      <img src={img} alt={text} style={{ width: '100%', height: '100%', objectFit: 'cover', position: 'absolute', inset: 0 }} />
      <div style={{ position: 'absolute', inset: 0, background: 'linear-gradient(90deg, rgba(0,0,0,0.35), rgba(0,0,0,0))' }} />
      <div style={{ position: 'relative', color: '#fff', fontSize: 30, fontStyle: 'italic', fontWeight: 500,
        textShadow: '0 2px 10px rgba(0,0,0,0.4)', textAlign: 'center', padding: '0 24px' }}>
        {text}
      </div>
    </div>
  );
}

// Collapsible so a long Brand/Material list never pushes the sidebar taller
// than the viewport — with `position: sticky` the only way to reach a
// filter buried at the bottom used to be scrolling past the entire
// (potentially very long, infinite-scroll) product grid first.
function FilterGroup({ label, name, options, active, onSet, defaultOpen }) {
  const [open, setOpen] = useState(!!defaultOpen || !!active);
  // A filter picked elsewhere (header mega-menu, a banner link) can land on
  // this page with e.g. Brand already active while its section is still
  // collapsed by default — auto-expand so the active pick is never hidden.
  useEffect(() => { if (active) setOpen(true); }, [active]);
  if (!options || !options.length) return null;
  return (
    <div style={{ borderTop: '1px solid #f2f2f2' }}>
      <button onClick={() => setOpen(o => !o)}
        style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: '100%',
          background: 'none', border: 'none', padding: '11px 0', cursor: 'pointer', textAlign: 'left' }}>
        <span style={{ fontSize: 12.5, fontWeight: 600, color: '#555', textTransform: 'uppercase', letterSpacing: 0.4 }}>
          {label}{active ? ` · ${active}` : ''}
        </span>
        <i className={`ti ti-chevron-${open ? 'up' : 'down'}`} style={{ fontSize: 15, color: '#999' }} />
      </button>
      {open && (
        <div style={{ paddingBottom: 12 }}>
          {options.map(opt => (
            <label key={opt} style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '3px 0', cursor: 'pointer', fontSize: 13,
              color: active === opt ? 'var(--sf-primary)' : '#666', fontWeight: active === opt ? 600 : 400 }}>
              <input type="radio" checked={active === opt} onChange={() => onSet(name, opt)}
                style={{ accentColor: 'var(--sf-primary)' }} />
              {opt}
            </label>
          ))}
        </div>
      )}
    </div>
  );
}
